// SPEC-57 to SPEC-61 · the words of « Momento do ciclo » on a bilan
// presencial: the pill in the bilan's header and the mark on its row of the
// list (SPEC-58), the side panel with its calendar (SPEC-59) and its other
// situations (SPEC-60), and the reminder at the end of phase 1 (SPEC-61).
// PT first (BRAND.md), FR and EN key for key. The phase is an estimate,
// never a diagnosis (BRAND.md §6): the words say « estimada », « previsto »,
// « aproximação ». The bilan's file is the lib's, in PT whatever the
// admin's language.

import type { Words } from "../shell";

/** A LEAF-Q answer's words in the panel's summary of it, by question and option id. */
const leafqPt = {
  pill: "toma a pílula",
  otherHormonal: (method: string) => `contraceção hormonal: ${method}`,
  otherHormonalUnnamed: "outro contracetivo hormonal",
  noHormonal: "sem contraceção hormonal",
  neverHadPeriod: "ainda não teve o primeiro período",
  bleeding: {
    "1-2": "período de 1 a 2 dias",
    "3-4": "período de 3 a 4 dias",
    "5-6": "período de 5 a 6 dias",
    "7-8": "período de 7 a 8 dias",
    "9-mais": "período de 9 dias ou mais",
  },
  regular: {
    "quase-sempre": "ciclo quase sempre regular",
    "quase-nunca": "ciclo quase nunca regular",
  },
  lastPeriod: {
    "1-2-meses": "último período há 1 a 2 meses",
    "3-4-meses": "último período há 3 a 4 meses",
    "5-6-meses": "último período há 5 a 6 meses",
    "mais-6-meses": "último período há mais de 6 meses",
    "12-meses-ou-mais": "último período há 12 meses ou mais",
    gravida: "grávida",
  },
  amenorrheaNow: "sem período neste momento",
  nothing: "nada sobre o ciclo",
} as const;

export const cyclePt = {
  // --- The pill and the list (SPEC-58) ---
  /** « Ciclo · D19 · fase lútea ». */
  stamp: (mark: string) => `Ciclo · ${mark}`,
  empty: "por preencher",
  notRecorded: "não registado",
  /** A natural cycle on the pill: « D19 · fase lútea ». */
  natural: (day: number, phase: string) => `D${day} · ${phase}`,
  /** The phase in lower case, on the pill and in a sentence. */
  phaseWords: {
    menstrual: "fase menstrual",
    folicular: "fase folicular",
    ovulacao: "ovulação estimada",
    lutea: "fase lútea",
    alem: "para lá do ciclo habitual",
  },
  /** A method in lower case: « Ciclo · anel », « Contraceção · pílula ». */
  methodWords: {
    pilula: "pílula",
    anel: "anel",
    diu: "diu hormonal",
    implante: "implante",
    adesivo: "adesivo",
    outro: "outro método",
  },
  pillMomentWords: {
    ativos: "comprimidos ativos",
    pausa: "pausa ou placebo",
    "nao-sabe": "não sabe",
  },
  /** « pílula, comprimidos ativos ». */
  pill: (method: string, moment: string) => `${method}, ${moment}`,
  sinceWords: {
    "menos-3-meses": "menos de 3 meses",
    "3-6-meses": "3 a 6 meses",
    "mais-6-meses": "mais de 6 meses",
    "nunca-teve": "ainda não teve o primeiro",
  },
  /** « sem período, 3 a 6 meses ». */
  noPeriod: (since: string) => `sem período, ${since}`,
  unknown: "desconhecido",
  /** What a screen reader hears on the pill, empty and filled. */
  openEmpty: "Momento do ciclo por preencher. Abrir o calendário",
  openFilled: (mark: string) => `Momento do ciclo: ${mark}. Corrigir no calendário`,
  /** A natural cycle for a screen reader: « dia 19, fase lútea ». */
  naturalSpoken: (day: number, phase: string) => `dia ${day}, ${phase}`,
  /** The mark after the date on a row of the list: « D19 lútea », « pílula », « sem período ». */
  list: {
    natural: (day: number, phase: string) => `D${day} ${phase}`,
    phases: {
      menstrual: "menstrual",
      folicular: "folicular",
      ovulacao: "ovulação",
      lutea: "lútea",
      alem: "além do ciclo",
    },
    noPeriod: "sem período",
    unknown: "ciclo desconhecido",
  },

  // --- The panel (SPEC-59, SPEC-60) ---
  title: "Momento do ciclo",
  close: "Fechar",
  /** « Beatriz Costa · ter 3 nov 2026, dia da sessão ». */
  meta: (athlete: string, date: string) => `${athlete} · ${date}, dia da sessão`,
  situation: "Situação da atleta",
  situations: {
    natural: "Ciclo natural",
    hormonal: "Contraceção hormonal",
    none: "Sem período",
    unknown: "Não sabe ou prefere não dizer",
  },
  inApp: "Na app",
  /** « Na app · LEAF-Q, 12 out », set in capitals by the screen. */
  inAppLeafq: (date: string) => `Na app · LEAF-Q, ${date}`,
  noLeafq: "LEAF-Q por fazer: nada para preencher.",
  leafq: leafqPt,

  firstDay: "Primeiro dia do último período",
  firstDayHelp: "Pergunta à atleta e toca no dia em que começou.",
  months: ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"],
  /** « outubro 2026 ». */
  monthTitle: (month: string, year: number) => `${month} ${year}`,
  previousMonth: "Mês anterior",
  nextMonth: "Mês seguinte",
  /** A day of the calendar for a screen reader: « 16 de outubro ». */
  day: (day: number, month: string) => `${day} de ${month}`,
  dayFirst: "primeiro dia do último período",
  daySession: "dia da sessão",
  legend: {
    period: "Período",
    luteal: "Fase lútea",
    ovulation: "Ovulação estimada",
    next: "Próximo previsto",
    session: "Dia da sessão",
  },

  cycleLength: "Duração do ciclo",
  periodLength: "Duração do período",
  days: (n: number) => (n === 1 ? "1 dia" : `${n} dias`),
  shorterCycle: "Ciclo mais curto",
  longerCycle: "Ciclo mais longo",
  shorterPeriod: "Período mais curto",
  longerPeriod: "Período mais longo",
  regular: "Ciclo regular?",
  regularities: { sim: "Sim", nao: "Não", "nao-sabe": "Não sabe" },

  /** The result card's label: « Momento do ciclo · ter 3 nov », set in capitals by the screen. */
  result: (date: string) => `Momento do ciclo · ${date}`,
  /** « ter 3 nov ». */
  sessionDay: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,
  /** « 16 out ». */
  dayMonth: (day: number, month: string) => `${day} ${month}`,
  phases: {
    menstrual: "Fase menstrual",
    folicular: "Fase folicular",
    ovulacao: "Ovulação estimada",
    lutea: "Fase lútea",
    alem: "Para lá do ciclo habitual",
  },
  /** The big number and what follows it: « D19 », « / 28 ». */
  dayNumber: (day: number) => `D${day}`,
  outOf: (length: number) => `/ ${length}`,
  /** The bar's three segments. */
  bar: { menstrual: "Menstrual", folicular: "Folicular", lutea: "Lútea" },
  lastAndNext: (last: string, next: string) => `Último período a ${last} · próximo previsto a ${next}.`,
  beyond: (length: number) => `Passou a duração habitual de ${length} dias. Confirma a data com a atleta, ou escolhe Sem período.`,
  irregular: "Com um ciclo irregular, a fase é só uma aproximação.",
  regularityUnknown: "Sem saber se o ciclo é regular, a fase é só uma aproximação.",
  toMark: "Por marcar",
  toMarkHelp: "Toca no calendário no primeiro dia do último período.",

  method: "Método",
  methods: {
    pilula: "Pílula",
    anel: "Anel",
    diu: "DIU hormonal",
    implante: "Implante",
    adesivo: "Adesivo",
    outro: "Outro",
  },
  pillMoment: "Neste momento",
  pillMoments: {
    ativos: "Comprimidos ativos",
    pausa: "Pausa ou placebo",
    "nao-sabe": "Não sabe",
  },
  hormonalTitle: "Contraceção hormonal",
  /** « Contraceção · pílula ». */
  hormonalWith: (method: string) => `Contraceção · ${method}`,
  hormonalLine: "Sem fase natural para estimar. O bilan regista o método e, com a pílula, a fase da toma.",
  since: "Há quanto tempo?",
  sinces: {
    "menos-3-meses": "Menos de 3 meses",
    "3-6-meses": "3 a 6 meses",
    "mais-6-meses": "Mais de 6 meses",
    "nunca-teve": "Ainda não teve o primeiro",
  },
  sinceTags: {
    "menos-3-meses": "Há menos de 3 meses",
    "3-6-meses": "Há 3 a 6 meses",
    "mais-6-meses": "Há mais de 6 meses",
    "nunca-teve": "Ainda não teve o primeiro",
  },
  noPeriodTitle: "Sem período",
  noPeriodLine: "O bilan regista há quanto tempo, como a atleta o diz.",
  unknownTitle: "Desconhecido",
  unknownLine: "A atleta não sabe ou prefere não dizer. O bilan fica marcado assim.",

  footer: "A marca fica no bilan e no ficheiro exportado. Corrige-se enquanto o bilan estiver em curso.",
  save: "Guardar",
  saving: "A guardar…",
  saveFailed: "Não consegui guardar. Tenta outra vez.",
  notFound: "Este bilan já não existe.",
  /** « Guardar » pressed while the situation chosen misses a field. */
  missing: {
    firstDay: "Toca no calendário no primeiro dia do último período.",
    method: "Escolhe o método.",
    pillMoment: "Escolhe em que momento da toma está.",
    since: "Escolhe há quanto tempo.",
  },

  // --- The reminder (SPEC-61) ---
  reminder: "Falta o momento do ciclo.",
  reminderFill: "Preencher",
  reminderLater: "Mais tarde",
} as const;

// French typography: a narrow no-break space (U+202F) before ? ! ; : and
// inside « », a curly apostrophe.
export const cycleFr: Words<typeof cyclePt> = {
  stamp: (mark: string) => `Cycle · ${mark}`,
  empty: "à remplir",
  notRecorded: "non renseigné",
  natural: (day: number, phase: string) => `J${day} · ${phase}`,
  phaseWords: {
    menstrual: "phase menstruelle",
    folicular: "phase folliculaire",
    ovulacao: "ovulation estimée",
    lutea: "phase lutéale",
    alem: "au-delà du cycle habituel",
  },
  methodWords: {
    pilula: "pilule",
    anel: "anneau",
    diu: "DIU hormonal",
    implante: "implant",
    adesivo: "patch",
    outro: "autre méthode",
  },
  pillMomentWords: {
    ativos: "comprimés actifs",
    pausa: "pause ou placebo",
    "nao-sabe": "ne sait pas",
  },
  pill: (method: string, moment: string) => `${method}, ${moment}`,
  sinceWords: {
    "menos-3-meses": "moins de 3 mois",
    "3-6-meses": "3 à 6 mois",
    "mais-6-meses": "plus de 6 mois",
    "nunca-teve": "pas encore eu ses premières règles",
  },
  noPeriod: (since: string) => `sans règles, ${since}`,
  unknown: "inconnu",
  openEmpty: "Moment du cycle à remplir. Ouvrir le calendrier",
  openFilled: (mark: string) => `Moment du cycle : ${mark}. Corriger dans le calendrier`,
  naturalSpoken: (day: number, phase: string) => `jour ${day}, ${phase}`,
  list: {
    natural: (day: number, phase: string) => `J${day} ${phase}`,
    phases: {
      menstrual: "menstruelle",
      folicular: "folliculaire",
      ovulacao: "ovulation",
      lutea: "lutéale",
      alem: "hors cycle",
    },
    noPeriod: "sans règles",
    unknown: "cycle inconnu",
  },

  title: "Moment du cycle",
  close: "Fermer",
  meta: (athlete: string, date: string) => `${athlete} · ${date}, jour de la séance`,
  situation: "Situation de l’athlète",
  situations: {
    natural: "Cycle naturel",
    hormonal: "Contraception hormonale",
    none: "Sans règles",
    unknown: "Ne sait pas ou préfère ne pas dire",
  },
  inApp: "Dans l’app",
  inAppLeafq: (date: string) => `Dans l’app · LEAF-Q, ${date}`,
  noLeafq: "LEAF-Q à faire : rien à préremplir.",
  leafq: {
    pill: "prend la pilule",
    otherHormonal: (method: string) => `contraception hormonale : ${method}`,
    otherHormonalUnnamed: "autre contraceptif hormonal",
    noHormonal: "sans contraception hormonale",
    neverHadPeriod: "n’a pas encore eu ses premières règles",
    bleeding: {
      "1-2": "règles de 1 à 2 jours",
      "3-4": "règles de 3 à 4 jours",
      "5-6": "règles de 5 à 6 jours",
      "7-8": "règles de 7 à 8 jours",
      "9-mais": "règles de 9 jours ou plus",
    },
    regular: {
      "quase-sempre": "cycle presque toujours régulier",
      "quase-nunca": "cycle presque jamais régulier",
    },
    lastPeriod: {
      "1-2-meses": "dernières règles il y a 1 à 2 mois",
      "3-4-meses": "dernières règles il y a 3 à 4 mois",
      "5-6-meses": "dernières règles il y a 5 à 6 mois",
      "mais-6-meses": "dernières règles il y a plus de 6 mois",
      "12-meses-ou-mais": "dernières règles il y a 12 mois ou plus",
      gravida: "enceinte",
    },
    amenorrheaNow: "sans règles en ce moment",
    nothing: "rien sur le cycle",
  },

  firstDay: "Premier jour des dernières règles",
  firstDayHelp: "Demande à l’athlète et touche le jour où elles ont commencé.",
  months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
  monthTitle: (month: string, year: number) => `${month} ${year}`,
  previousMonth: "Mois précédent",
  nextMonth: "Mois suivant",
  day: (day: number, month: string) => `${day} ${month}`,
  dayFirst: "premier jour des dernières règles",
  daySession: "jour de la séance",
  legend: {
    period: "Règles",
    luteal: "Phase lutéale",
    ovulation: "Ovulation estimée",
    next: "Prochaines prévues",
    session: "Jour de la séance",
  },

  cycleLength: "Durée du cycle",
  periodLength: "Durée des règles",
  days: (n: number) => (n === 1 ? "1 jour" : `${n} jours`),
  shorterCycle: "Cycle plus court",
  longerCycle: "Cycle plus long",
  shorterPeriod: "Règles plus courtes",
  longerPeriod: "Règles plus longues",
  regular: "Cycle régulier ?",
  regularities: { sim: "Oui", nao: "Non", "nao-sabe": "Ne sait pas" },

  result: (date: string) => `Moment du cycle · ${date}`,
  sessionDay: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,
  dayMonth: (day: number, month: string) => `${day} ${month}`,
  phases: {
    menstrual: "Phase menstruelle",
    folicular: "Phase folliculaire",
    ovulacao: "Ovulation estimée",
    lutea: "Phase lutéale",
    alem: "Au-delà du cycle habituel",
  },
  dayNumber: (day: number) => `J${day}`,
  outOf: (length: number) => `/ ${length}`,
  bar: {
    menstrual: "Menstruelle",
    folicular: "Folliculaire",
    lutea: "Lutéale",
  },
  lastAndNext: (last: string, next: string) => `Dernières règles le ${last} · prochaines prévues le ${next}.`,
  beyond: (length: number) =>
    `La durée habituelle de ${length} jours est dépassée. Confirme la date avec l’athlète, ou choisis Sans règles.`,
  irregular: "Avec un cycle irrégulier, la phase n’est qu’une approximation.",
  regularityUnknown: "Sans savoir si le cycle est régulier, la phase n’est qu’une approximation.",
  toMark: "À marquer",
  toMarkHelp: "Touche dans le calendrier le premier jour des dernières règles.",

  method: "Méthode",
  methods: {
    pilula: "Pilule",
    anel: "Anneau",
    diu: "DIU hormonal",
    implante: "Implant",
    adesivo: "Patch",
    outro: "Autre",
  },
  pillMoment: "En ce moment",
  pillMoments: {
    ativos: "Comprimés actifs",
    pausa: "Pause ou placebo",
    "nao-sabe": "Ne sait pas",
  },
  hormonalTitle: "Contraception hormonale",
  hormonalWith: (method: string) => `Contraception · ${method}`,
  hormonalLine: "Pas de phase naturelle à estimer. Le bilan note la méthode et, avec la pilule, la phase de prise.",
  since: "Depuis combien de temps ?",
  sinces: {
    "menos-3-meses": "Moins de 3 mois",
    "3-6-meses": "3 à 6 mois",
    "mais-6-meses": "Plus de 6 mois",
    "nunca-teve": "Pas encore eu ses premières règles",
  },
  sinceTags: {
    "menos-3-meses": "Depuis moins de 3 mois",
    "3-6-meses": "Depuis 3 à 6 mois",
    "mais-6-meses": "Depuis plus de 6 mois",
    "nunca-teve": "Pas encore eu ses premières règles",
  },
  noPeriodTitle: "Sans règles",
  noPeriodLine: "Le bilan note depuis combien de temps, comme l’athlète le dit.",
  unknownTitle: "Inconnu",
  unknownLine: "L’athlète ne sait pas ou préfère ne pas dire. Le bilan est marqué ainsi.",

  footer: "La marque reste dans le bilan et dans le fichier exporté. Elle se corrige tant que le bilan est en cours.",
  save: "Enregistrer",
  saving: "Enregistrement…",
  saveFailed: "Je n’ai pas pu enregistrer. Réessaie.",
  notFound: "Ce bilan n’existe plus.",
  missing: {
    firstDay: "Touche dans le calendrier le premier jour des dernières règles.",
    method: "Choisis la méthode.",
    pillMoment: "Choisis où elle en est de sa prise.",
    since: "Choisis depuis combien de temps.",
  },

  reminder: "Il manque le moment du cycle.",
  reminderFill: "Remplir",
  reminderLater: "Plus tard",
};

export const cycleEn: Words<typeof cyclePt> = {
  stamp: (mark: string) => `Cycle · ${mark}`,
  empty: "to fill in",
  notRecorded: "not recorded",
  natural: (day: number, phase: string) => `D${day} · ${phase}`,
  phaseWords: {
    menstrual: "menstrual phase",
    folicular: "follicular phase",
    ovulacao: "estimated ovulation",
    lutea: "luteal phase",
    alem: "past the usual cycle",
  },
  methodWords: {
    pilula: "pill",
    anel: "ring",
    diu: "hormonal IUD",
    implante: "implant",
    adesivo: "patch",
    outro: "other method",
  },
  pillMomentWords: {
    ativos: "active pills",
    pausa: "break or placebo",
    "nao-sabe": "doesn’t know",
  },
  pill: (method: string, moment: string) => `${method}, ${moment}`,
  sinceWords: {
    "menos-3-meses": "under 3 months",
    "3-6-meses": "3 to 6 months",
    "mais-6-meses": "over 6 months",
    "nunca-teve": "no first period yet",
  },
  noPeriod: (since: string) => `no period, ${since}`,
  unknown: "unknown",
  openEmpty: "Cycle timing to fill in. Open the calendar",
  openFilled: (mark: string) => `Cycle timing: ${mark}. Correct it in the calendar`,
  naturalSpoken: (day: number, phase: string) => `day ${day}, ${phase}`,
  list: {
    natural: (day: number, phase: string) => `D${day} ${phase}`,
    phases: {
      menstrual: "menstrual",
      folicular: "follicular",
      ovulacao: "ovulation",
      lutea: "luteal",
      alem: "past cycle",
    },
    noPeriod: "no period",
    unknown: "cycle unknown",
  },

  title: "Cycle timing",
  close: "Close",
  meta: (athlete: string, date: string) => `${athlete} · ${date}, session day`,
  situation: "The athlete’s situation",
  situations: {
    natural: "Natural cycle",
    hormonal: "Hormonal contraception",
    none: "No period",
    unknown: "Doesn’t know or prefers not to say",
  },
  inApp: "In the app",
  inAppLeafq: (date: string) => `In the app · LEAF-Q, ${date}`,
  noLeafq: "LEAF-Q not done: nothing to fill in.",
  leafq: {
    pill: "takes the pill",
    otherHormonal: (method: string) => `hormonal contraception: ${method}`,
    otherHormonalUnnamed: "another hormonal contraceptive",
    noHormonal: "no hormonal contraception",
    neverHadPeriod: "no first period yet",
    bleeding: {
      "1-2": "period of 1 to 2 days",
      "3-4": "period of 3 to 4 days",
      "5-6": "period of 5 to 6 days",
      "7-8": "period of 7 to 8 days",
      "9-mais": "period of 9 days or more",
    },
    regular: {
      "quase-sempre": "cycle almost always regular",
      "quase-nunca": "cycle almost never regular",
    },
    lastPeriod: {
      "1-2-meses": "last period 1 to 2 months ago",
      "3-4-meses": "last period 3 to 4 months ago",
      "5-6-meses": "last period 5 to 6 months ago",
      "mais-6-meses": "last period over 6 months ago",
      "12-meses-ou-mais": "last period 12 months ago or more",
      gravida: "pregnant",
    },
    amenorrheaNow: "no period at the moment",
    nothing: "nothing about the cycle",
  },

  firstDay: "First day of the last period",
  firstDayHelp: "Ask the athlete and tap the day it started.",
  months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  monthTitle: (month: string, year: number) => `${month} ${year}`,
  previousMonth: "Previous month",
  nextMonth: "Next month",
  day: (day: number, month: string) => `${month} ${day}`,
  dayFirst: "first day of the last period",
  daySession: "session day",
  legend: {
    period: "Period",
    luteal: "Luteal phase",
    ovulation: "Estimated ovulation",
    next: "Next expected",
    session: "Session day",
  },

  cycleLength: "Cycle length",
  periodLength: "Period length",
  days: (n: number) => (n === 1 ? "1 day" : `${n} days`),
  shorterCycle: "Shorter cycle",
  longerCycle: "Longer cycle",
  shorterPeriod: "Shorter period",
  longerPeriod: "Longer period",
  regular: "Regular cycle?",
  regularities: { sim: "Yes", nao: "No", "nao-sabe": "Doesn’t know" },

  result: (date: string) => `Cycle timing · ${date}`,
  sessionDay: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,
  dayMonth: (day: number, month: string) => `${day} ${month}`,
  phases: {
    menstrual: "Menstrual phase",
    folicular: "Follicular phase",
    ovulacao: "Estimated ovulation",
    lutea: "Luteal phase",
    alem: "Past the usual cycle",
  },
  dayNumber: (day: number) => `D${day}`,
  outOf: (length: number) => `/ ${length}`,
  bar: { menstrual: "Menstrual", folicular: "Follicular", lutea: "Luteal" },
  lastAndNext: (last: string, next: string) => `Last period on ${last} · next expected on ${next}.`,
  beyond: (length: number) => `Past the usual ${length} days. Check the date with the athlete, or choose No period.`,
  irregular: "With an irregular cycle, the phase is only an approximation.",
  regularityUnknown: "Without knowing whether the cycle is regular, the phase is only an approximation.",
  toMark: "Not marked yet",
  toMarkHelp: "Tap the first day of the last period on the calendar.",

  method: "Method",
  methods: {
    pilula: "Pill",
    anel: "Ring",
    diu: "Hormonal IUD",
    implante: "Implant",
    adesivo: "Patch",
    outro: "Other",
  },
  pillMoment: "Right now",
  pillMoments: {
    ativos: "Active pills",
    pausa: "Break or placebo",
    "nao-sabe": "Doesn’t know",
  },
  hormonalTitle: "Hormonal contraception",
  hormonalWith: (method: string) => `Contraception · ${method}`,
  hormonalLine: "No natural phase to estimate. The assessment records the method and, with the pill, where she is in the pack.",
  since: "For how long?",
  sinces: {
    "menos-3-meses": "Under 3 months",
    "3-6-meses": "3 to 6 months",
    "mais-6-meses": "Over 6 months",
    "nunca-teve": "No first period yet",
  },
  sinceTags: {
    "menos-3-meses": "For under 3 months",
    "3-6-meses": "For 3 to 6 months",
    "mais-6-meses": "For over 6 months",
    "nunca-teve": "No first period yet",
  },
  noPeriodTitle: "No period",
  noPeriodLine: "The assessment records for how long, as the athlete puts it.",
  unknownTitle: "Unknown",
  unknownLine: "The athlete doesn’t know or prefers not to say. The assessment is marked that way.",

  footer: "The mark stays on the assessment and in the exported file. It can be corrected while the assessment is in progress.",
  save: "Save",
  saving: "Saving…",
  saveFailed: "I couldn’t save. Try again.",
  notFound: "This assessment no longer exists.",
  missing: {
    firstDay: "Tap the first day of the last period on the calendar.",
    method: "Choose the method.",
    pillMoment: "Choose where she is in the pack.",
    since: "Choose for how long.",
  },

  reminder: "The cycle timing is missing.",
  reminderFill: "Fill in",
  reminderLater: "Later",
};
